import { useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const absUrl = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const TYPO_FONTS = {
  elegant: "'Georgia', 'Times New Roman', serif",
  bold: "'Arial Black', 'Archivo Black', sans-serif",
  friendly: "'Trebuchet MS', 'Verdana', sans-serif",
  minimal: "'Helvetica Neue', 'Arial', sans-serif",
};

export const EditableText = ({ value, path, onEdit, className = "", as: Tag = "span", placeholder = "…", multiline = false, style }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  if (!onEdit) return <Tag className={className} style={style}>{value || ""}</Tag>;
  if (editing) {
    const commit = () => { setEditing(false); if (draft !== value) onEdit(path, draft); };
    const Props = {
      autoFocus: true, value: draft, onChange: (e) => setDraft(e.target.value), onBlur: commit,
      onKeyDown: (e) => { if (e.key === "Enter" && !multiline) { e.preventDefault(); commit(); } if (e.key === "Escape") setEditing(false); },
      className: `${className} w-full bg-white/80 outline outline-2 outline-[#E8A5A0] rounded px-1`, style,
      "data-testid": `lp-edit-input-${path.replace(/\./g, "-")}`,
    };
    return multiline ? <textarea rows={3} {...Props} /> : <input {...Props} />;
  }
  return (
    <Tag
      className={`${className} cursor-text hover:outline hover:outline-1 hover:outline-dashed hover:outline-[#E8A5A0] rounded`}
      style={style}
      onClick={() => { setDraft(value || ""); setEditing(true); }}
      data-testid={`lp-slot-${path.replace(/\./g, "-")}`}
    >
      {value || <span className="opacity-40">{placeholder}</span>}
    </Tag>
  );
};

const patternBg = (p, s) => ({
  backgroundColor: "#FBFAF7",
  backgroundImage: `radial-gradient(circle at 15% 25%, ${p}1f 0, transparent 45%), radial-gradient(circle at 85% 75%, ${s}1f 0, transparent 45%), radial-gradient(${p}14 1px, transparent 1px)`,
  backgroundSize: "auto, auto, 22px 22px",
});

const HeroWrap = ({ image, p, s, children }) => {
  if (image?.mode !== "pattern" && image?.url) {
    return (
      <div className="relative" style={{ backgroundImage: `url(${absUrl(image.url)})`, backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,16,20,0.55), rgba(15,16,20,0.75))" }} />
        <div className="relative text-white">{children}</div>
      </div>
    );
  }
  return <div style={patternBg(p, s)}>{children}</div>;
};

const Btn = ({ children, primary, href, onClick, testid }) => {
  const cls = "inline-block rounded-full px-7 py-3.5 text-[15px] font-bold transition-transform hover:scale-[1.02] text-center";
  const style = { background: primary, color: "#fff", boxShadow: `0 8px 24px ${primary}55` };
  return href ? (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className={cls} style={style} data-testid={testid}>{children}</a>
  ) : (
    <button onClick={onClick} className={cls} style={style} data-testid={testid}>{children}</button>
  );
};

const Trio = ({ items, path, onEdit, dark }) => (
  <div className="grid sm:grid-cols-3 gap-5 max-w-5xl mx-auto px-6">
    {(items || []).map((b, i) => (
      <div key={i} className={`rounded-2xl p-6 ${dark ? "bg-white/10" : "bg-white shadow-[0_2px_16px_rgba(29,30,38,0.06)]"}`}>
        <EditableText as="p" className="font-extrabold text-[16px]" value={b.title} path={`${path}.${i}.title`} onEdit={onEdit} />
        <EditableText as="p" className="text-[14px] mt-2 leading-relaxed opacity-75 block" value={b.text} path={`${path}.${i}.text`} onEdit={onEdit} multiline />
      </div>
    ))}
  </div>
);

const FAQ = ({ items, onEdit }) => (
  <div className="max-w-3xl mx-auto px-6 space-y-3">
    {(items || []).map((f, i) => (
      <div key={i} className="rounded-xl bg-white shadow-[0_1px_8px_rgba(29,30,38,0.05)] p-5">
        <EditableText as="p" className="font-bold text-[15px]" value={f.q} path={`faq.${i}.q`} onEdit={onEdit} />
        <EditableText as="p" className="text-[13.5px] mt-1.5 opacity-70 block leading-relaxed" value={f.a} path={`faq.${i}.a`} onEdit={onEdit} multiline />
      </div>
    ))}
  </div>
);

const Quotes = ({ items, path, onEdit, onAdd, onRemove, label }) => {
  if (!onEdit && !(items || []).length) return null;
  return (
    <div className="max-w-4xl mx-auto px-6">
      <div className="grid sm:grid-cols-2 gap-5">
        {(items || []).map((t, i) => (
          <div key={i} className="rounded-2xl bg-white shadow-[0_2px_16px_rgba(29,30,38,0.06)] p-6 relative">
            {onEdit && <button onClick={() => onRemove(i)} className="absolute top-3 right-3 text-[#A3A19B] hover:text-[#1D1E26] text-[13px]" data-testid={`lp-remove-${path}-${i}`}>✕</button>}
            <EditableText as="p" className="text-[14.5px] italic leading-relaxed block" value={t.quote} path={`${path}.${i}.quote`} onEdit={onEdit} multiline placeholder="“Quote…”" />
            <EditableText as="p" className="text-[12.5px] font-bold mt-3 opacity-60" value={t.name} path={`${path}.${i}.name`} onEdit={onEdit} placeholder="Name" />
          </div>
        ))}
      </div>
      {onEdit && (
        <button onClick={onAdd} className="mt-4 text-[13px] font-bold underline opacity-70 hover:opacity-100" data-testid={`lp-add-${path}`}>+ Add {label}</button>
      )}
    </div>
  );
};

const Gallery = ({ urls }) => {
  if (!(urls || []).length) return null;
  return (
    <div className="flex gap-3 overflow-x-auto px-6 max-w-5xl mx-auto pb-2">
      {urls.map((u, i) => (
        <img key={i} src={absUrl(u)} alt="" className="h-52 rounded-xl object-cover shrink-0" data-testid={`lp-gallery-img-${i}`} />
      ))}
    </div>
  );
};

const LeadForm = ({ template, content, primary, onSubmitLead, submitting, submitted, editable, ownerName, ownerEmail }) => {
  const [f, setF] = useState({ name: "", email: "", phone: "", datetime_pref: "", gdpr: false, website: "" });
  const form = content.form || {};
  if (submitted) {
    return (
      <div className="max-w-xl mx-auto px-6 text-center py-4" data-testid="lp-thank-you">
        <p className="text-3xl">✓</p>
        <p className="text-[16px] font-bold mt-3">{form.thank_you || "Thank you — we'll be in touch."}</p>
      </div>
    );
  }
  const field = "w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-[14px] outline-none focus:border-black/30";
  const showEmail = template !== "booking";
  const showPhone = template === "lead" || template === "booking";
  const showName = template !== "waitlist";
  return (
    <form
      className="max-w-xl mx-auto px-6 space-y-3"
      onSubmit={(e) => { e.preventDefault(); if (!editable) onSubmitLead(f); }}
      data-testid="lp-form"
    >
      <p className="text-xl font-extrabold text-center">
        <EditableText value={form.title} path="form.title" onEdit={editable} />
      </p>
      <input type="text" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      {showName && <input className={field} placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="lp-form-name" />}
      {showEmail && <input className={field} type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="lp-form-email" />}
      {showPhone && <input className={field} placeholder={template === "booking" ? "Phone" : "Phone (optional)"} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="lp-form-phone" />}
      {template === "booking" && <input className={field} placeholder="Preferred date & time" value={f.datetime_pref} onChange={(e) => setF({ ...f, datetime_pref: e.target.value })} data-testid="lp-form-datetime" />}
      <label className="flex items-start gap-2.5 text-[12px] opacity-70 cursor-pointer">
        <input type="checkbox" checked={f.gdpr} onChange={(e) => setF({ ...f, gdpr: e.target.checked })} className="mt-0.5" data-testid="lp-form-gdpr" />
        <span>I consent to the processing of my personal data to be contacted about this request (GDPR).</span>
      </label>
      <button type="submit" disabled={submitting || !!editable} className="w-full rounded-full py-3.5 text-[15px] font-bold text-white transition-transform hover:scale-[1.01]" style={{ background: primary, boxShadow: `0 8px 24px ${primary}55` }} data-testid="lp-form-submit">
        {submitting ? "Sending…" : <EditableText value={form.button} path="form.button" onEdit={editable} />}
      </button>
      <p className="text-[10.5px] opacity-50 leading-relaxed pt-1" data-testid="lp-privacy-note">
        We collect the data you enter in this form to respond to your request / send you the newsletter. The data controller is {ownerName || "the owner of this page"}. You can access, correct, export or delete your data, or object, by writing to {ownerEmail || "the contact listed on this page"}. See the full privacy policy.
      </p>
    </form>
  );
};

const SectionTitle = ({ children }) => (
  <p className="text-center font-mono text-[10px] tracking-[0.2em] uppercase opacity-50 mb-8">{children}</p>
);

export default function TemplateRenderer({ landing, tokens, logoUrl, editable, onEdit, onArrayAdd, onArrayRemove, onSubmitLead, submitting, submitted }) {
  const { template, content = {}, sections = {}, images = {} } = landing;
  const p = tokens?.primary || "#1D1E26";
  const s = tokens?.secondary || "#E8A5A0";
  const font = TYPO_FONTS[tokens?.typography] || TYPO_FONTS.minimal;
  const on = (k) => sections[k] !== false;
  const scrollForm = () => document.getElementById("lp-form-anchor")?.scrollIntoView({ behavior: "smooth" });
  const offerCta = template === "offer" ? (landing.product_url || landing.brief?.product_url || "#") : null;
  const heroImg = images.hero;

  const Header = () => (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-black/5 px-5 py-3 flex items-center justify-between" data-testid="lp-header">
      <span className="flex items-center gap-2.5">
        {logoUrl && <img src={logoUrl} alt="" className="h-8 w-8 object-contain rounded" onError={(e) => { e.target.style.display = "none"; }} />}
        <span className="font-extrabold text-[15px]" style={{ fontFamily: font }}>{tokens?.business_name || ""}</span>
      </span>
      {template !== "waitlist" && (
        <Btn primary={p} onClick={offerCta ? undefined : scrollForm} href={offerCta || undefined} testid="lp-header-cta">
          <EditableText value={content.header?.cta} path="header.cta" onEdit={onEdit} />
        </Btn>
      )}
    </header>
  );

  const Hero = () => (
    <HeroWrap image={heroImg} p={p} s={s}>
      <div className={`px-6 pt-16 pb-20 text-center max-w-3xl mx-auto ${template === "waitlist" ? "pt-24 pb-24" : ""}`} data-testid="lp-hero">
        {template === "offer" && content.hero?.badge && (
          <span className="inline-block rounded-full px-4 py-1.5 text-[12px] font-extrabold text-white mb-5" style={{ background: s }}>
            <EditableText value={content.hero.badge} path="hero.badge" onEdit={onEdit} />
          </span>
        )}
        {template === "booking" && (
          <p className="text-[13px] font-bold uppercase tracking-widest opacity-70 mb-4">
            <EditableText value={content.hero?.location_line} path="hero.location_line" onEdit={onEdit} />
          </p>
        )}
        <EditableText as="h1" className="text-4xl sm:text-5xl font-extrabold leading-[1.08] block" style={{ fontFamily: font }} value={content.hero?.headline} path="hero.headline" onEdit={onEdit} multiline />
        <EditableText as="p" className="text-[16px] mt-5 opacity-80 leading-relaxed block max-w-xl mx-auto" value={content.hero?.subhead} path="hero.subhead" onEdit={onEdit} multiline />
        {template === "offer" && content.hero?.price_line && (
          <p className="text-2xl font-extrabold mt-5"><EditableText value={content.hero.price_line} path="hero.price_line" onEdit={onEdit} /></p>
        )}
        <div className="mt-8">
          <Btn primary={p} onClick={offerCta ? undefined : scrollForm} href={offerCta || undefined} testid="lp-hero-cta">
            <EditableText value={content.hero?.cta} path="hero.cta" onEdit={onEdit} />
          </Btn>
        </div>
        {content.hero?.trust_line && (
          <p className="text-[12.5px] mt-4 opacity-60"><EditableText value={content.hero.trust_line} path="hero.trust_line" onEdit={onEdit} /></p>
        )}
      </div>
    </HeroWrap>
  );

  const FinalCta = () => (
    <section className="py-16 text-center px-6" style={{ background: p }} data-testid="lp-final">
      <EditableText as="h2" className="text-3xl font-extrabold text-white block max-w-2xl mx-auto" style={{ fontFamily: font }} value={content.final?.headline} path="final.headline" onEdit={onEdit} multiline />
      <div className="mt-7">
        <Btn primary={s} onClick={offerCta ? undefined : scrollForm} href={offerCta || undefined} testid="lp-final-cta">
          <EditableText value={content.final?.cta} path="final.cta" onEdit={onEdit} />
        </Btn>
      </div>
    </section>
  );

  const FormSection = () => (
    <section id="lp-form-anchor" className="py-16" style={patternBg(p, s)} data-testid="lp-form-section">
      <LeadForm template={template} content={content} primary={p} onSubmitLead={onSubmitLead} submitting={submitting} submitted={submitted} editable={onEdit} ownerName={tokens?.business_name} ownerEmail={landing.owner_contact} />
    </section>
  );

  const Footer = () => (
    <footer className="py-8 text-center text-[11.5px] opacity-50" data-testid="lp-footer">
      © {new Date().getFullYear()} {tokens?.business_name} · Built with Agenzee
    </footer>
  );

  return (
    <div className="min-h-full text-[#171821]" style={{ fontFamily: "'Helvetica Neue', Arial, sans-serif", background: "#FFFFFF" }} data-testid={`lp-template-${template}`}>
      {template !== "waitlist" && <Header />}
      {on("hero") && <Hero />}

      {template === "lead" && (
        <>
          {on("benefits") && <section className="py-14" data-testid="lp-benefits"><SectionTitle>Why us</SectionTitle><Trio items={content.benefits} path="benefits" onEdit={onEdit} /></section>}
          {on("steps") && (
            <section className="py-14 bg-[#FAF9F6]" data-testid="lp-steps">
              <SectionTitle>How it works</SectionTitle>
              <div className="grid sm:grid-cols-3 gap-5 max-w-5xl mx-auto px-6">
                {(content.steps || []).map((st, i) => (
                  <div key={i} className="text-center px-4">
                    <span className="inline-flex w-10 h-10 rounded-full items-center justify-center text-white font-extrabold" style={{ background: p }}>{i + 1}</span>
                    <EditableText as="p" className="font-extrabold text-[15px] mt-3 block" value={st.title} path={`steps.${i}.title`} onEdit={onEdit} />
                    <EditableText as="p" className="text-[13.5px] mt-1.5 opacity-70 block" value={st.text} path={`steps.${i}.text`} onEdit={onEdit} multiline />
                  </div>
                ))}
              </div>
            </section>
          )}
          {on("testimonials") && (onEdit || (content.testimonials || []).length > 0) && (
            <section className="py-14" data-testid="lp-testimonials"><SectionTitle>What clients say</SectionTitle>
              <Quotes items={content.testimonials} path="testimonials" onEdit={onEdit} onAdd={() => onArrayAdd("testimonials", { quote: "", name: "" })} onRemove={(i) => onArrayRemove("testimonials", i)} label="testimonial" />
            </section>
          )}
          {on("faq") && <section className="py-14 bg-[#FAF9F6]" data-testid="lp-faq"><SectionTitle>Questions, answered</SectionTitle><FAQ items={content.faq} onEdit={onEdit} /></section>}
          {on("final") && <FinalCta />}
          {on("form") && <FormSection />}
        </>
      )}

      {template === "booking" && (
        <>
          {on("highlights") && <section className="py-14" data-testid="lp-highlights"><SectionTitle>Highlights</SectionTitle><Trio items={content.highlights} path="highlights" onEdit={onEdit} /></section>}
          {on("gallery") && (images.gallery || []).length > 0 && <section className="py-8" data-testid="lp-gallery"><Gallery urls={images.gallery} /></section>}
          {on("hours") && (
            <section className="py-14 bg-[#FAF9F6]" data-testid="lp-hours">
              <div className="max-w-4xl mx-auto px-6 grid sm:grid-cols-2 gap-8 items-start">
                <div>
                  <EditableText as="h3" className="text-xl font-extrabold block" style={{ fontFamily: font }} value={content.hours?.title} path="hours.title" onEdit={onEdit} />
                  <div className="mt-4 space-y-1.5">
                    {(content.hours?.lines || []).map((l, i) => (
                      <EditableText key={i} as="p" className="text-[14px] block" value={l} path={`hours.lines.${i}`} onEdit={onEdit} />
                    ))}
                  </div>
                  <EditableText as="p" className="text-[13.5px] mt-4 font-bold block" value={content.hours?.address} path="hours.address" onEdit={onEdit} />
                  {content.hours?.address && (
                    <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(content.hours.address)}`} target="_blank" rel="noreferrer" className="inline-block mt-3 text-[13px] font-bold underline" style={{ color: p }} data-testid="lp-directions">Get directions →</a>
                  )}
                </div>
                {content.hours?.address && (
                  <iframe title="map" src={`https://www.google.com/maps?q=${encodeURIComponent(content.hours.address)}&output=embed`} className="w-full h-56 rounded-2xl border-0" loading="lazy" data-testid="lp-map" />
                )}
              </div>
            </section>
          )}
          {on("reviews") && (onEdit || (content.reviews || []).length > 0) && (
            <section className="py-14" data-testid="lp-reviews"><SectionTitle>Reviews</SectionTitle>
              <Quotes items={content.reviews} path="reviews" onEdit={onEdit} onAdd={() => onArrayAdd("reviews", { quote: "", name: "" })} onRemove={(i) => onArrayRemove("reviews", i)} label="review" />
            </section>
          )}
          {on("form") && <FormSection />}
        </>
      )}

      {template === "offer" && (
        <>
          {on("benefits") && <section className="py-14" data-testid="lp-benefits"><SectionTitle>Why you'll love it</SectionTitle><Trio items={content.benefits} path="benefits" onEdit={onEdit} /></section>}
          {on("gallery") && (images.gallery || []).length > 0 && <section className="py-8" data-testid="lp-gallery"><Gallery urls={images.gallery} /></section>}
          {on("proof") && (onEdit || (content.proof || []).length > 0) && (
            <section className="py-14 bg-[#FAF9F6]" data-testid="lp-proof"><SectionTitle>Social proof</SectionTitle>
              <Quotes items={content.proof} path="proof" onEdit={onEdit} onAdd={() => onArrayAdd("proof", { quote: "", name: "" })} onRemove={(i) => onArrayRemove("proof", i)} label="proof" />
            </section>
          )}
          {on("guarantee") && (
            <section className="py-10" data-testid="lp-guarantee">
              <div className="max-w-5xl mx-auto px-6 grid sm:grid-cols-3 gap-4">
                {(content.guarantee || []).map((g, i) => (
                  <div key={i} className="rounded-xl border border-black/10 p-4 text-center">
                    <EditableText as="p" className="font-extrabold text-[14px] block" value={g.title} path={`guarantee.${i}.title`} onEdit={onEdit} />
                    <EditableText as="p" className="text-[12.5px] mt-1 opacity-70 block" value={g.text} path={`guarantee.${i}.text`} onEdit={onEdit} />
                  </div>
                ))}
              </div>
            </section>
          )}
          {on("faq") && <section className="py-14 bg-[#FAF9F6]" data-testid="lp-faq"><SectionTitle>Questions, answered</SectionTitle><FAQ items={content.faq} onEdit={onEdit} /></section>}
          {on("final") && <FinalCta />}
        </>
      )}

      {template === "waitlist" && (
        <>
          {on("benefits") && <section className="py-14" data-testid="lp-benefits"><SectionTitle>What's coming</SectionTitle><Trio items={content.benefits} path="benefits" onEdit={onEdit} /></section>}
          {on("form") && <FormSection />}
          {on("next") && (
            <section className="py-14 bg-[#FAF9F6]" data-testid="lp-next">
              <SectionTitle>What happens next</SectionTitle>
              <div className="grid sm:grid-cols-3 gap-5 max-w-5xl mx-auto px-6">
                {(content.next || []).map((st, i) => (
                  <div key={i} className="text-center px-4">
                    <span className="inline-flex w-10 h-10 rounded-full items-center justify-center text-white font-extrabold" style={{ background: p }}>{i + 1}</span>
                    <EditableText as="p" className="font-extrabold text-[15px] mt-3 block" value={st.title} path={`next.${i}.title`} onEdit={onEdit} />
                    <EditableText as="p" className="text-[13.5px] mt-1.5 opacity-70 block" value={st.text} path={`next.${i}.text`} onEdit={onEdit} multiline />
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      <Footer />

      {!onEdit && !submitted && (
        <div className="fixed bottom-0 inset-x-0 sm:hidden bg-white/95 backdrop-blur border-t border-black/10 p-3 z-50" data-testid="lp-sticky-cta">
          <Btn primary={p} onClick={offerCta ? undefined : scrollForm} href={offerCta || undefined} testid="lp-sticky-cta-btn">
            {content.hero?.cta || "Get started"}
          </Btn>
        </div>
      )}
    </div>
  );
}
